import React, { useEffect, useState } from "react";
import Button from "../../components/ui/Button";
import Input from "../../components/ui/Input";
import CustomDatePicker from "../../components/common/CustomDatePicker";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import Swal from "sweetalert2";
import "react-toastify/dist/ReactToastify.css";
import dayjs from "dayjs";

const API_BASE = "https://tyka.premierhostings.com/beta/api";
const today = dayjs().startOf("day");

const DiscountFormPage = () => {
  const navigate = useNavigate();
  const { id } = useParams(); // route: /discounts/edit/:id  (no id => add mode)
  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [loadingDiscount, setLoadingDiscount] = useState(isEdit);

  const [menuCategories, setMenuCategories] = useState([]);

  // Controlled form fields (works for both add and edit)
  const [name, setName] = useState("");
  const [type, setType] = useState("percentage");
  const [amount, setAmount] = useState("");
  const [active, setActive] = useState(true);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const [targetType, setTargetType] = useState("category");
  const [categoryId, setCategoryId] = useState("");
  const [subcategoryId, setSubcategoryId] = useState("");
  const [childCategoryId, setChildCategoryId] = useState("");

  // Existing discount-application row (edit mode)
  const [applicationId, setApplicationId] = useState(null);

  const getId = (item) =>
    item?.id ??
    item?.category_id ??
    item?.sub_category_id ??
    item?.subcategory_id ??
    item?.child_category_id ??
    "";

  const getName = (item) =>
    item?.name ??
    item?.category_name ??
    item?.sub_category_name ??
    item?.subcategory_name ??
    item?.child_category_name ??
    item?.title ??
    "Unnamed";

  const getMenuArray = (d) => {
    if (Array.isArray(d)) return d;
    if (Array.isArray(d?.data)) return d.data;
    if (Array.isArray(d?.data?.categories)) return d.data.categories;
    if (Array.isArray(d?.categories)) return d.categories;
    if (Array.isArray(d?.menu)) return d.menu;
    return [];
  };

  // ---------- Load menu ----------
  const fetchMenu = async () => {
    try {
      setLoadingOptions(true);
      const response = await axios.get(`${API_BASE}/menu`);
      setMenuCategories(getMenuArray(response.data));
    } catch (error) {
      console.error("Menu API error:", error.response?.data || error);
      toast.error("Failed to load menu categories.");
      setMenuCategories([]);
    } finally {
      setLoadingOptions(false);
    }
  };

  // ---------- Load existing discount (edit mode) ----------
  const fetchDiscount = async () => {
    try {
      setLoadingDiscount(true);
      const response = await axios.get(`${API_BASE}/discounts/${id}`);

      // Adjust if your API wraps the discount differently.
      const d =
        response.data?.discount ??
        response.data?.data?.discount ??
        response.data?.data ??
        response.data;

      setName(d?.name ?? "");
      setAmount(d?.amount ?? "");
      setActive(Number(d?.active) === 1 || d?.active === true);
      setStartDate(d?.start_date ? dayjs(d.start_date) : null);
      setEndDate(d?.end_date ? dayjs(d.end_date) : null);

      const tt = d?.target_type ?? "category";
      setTargetType(tt);
      setCategoryId(d?.category_id ? String(d.category_id) : "");
      setSubcategoryId(d?.subcategory_id ? String(d.subcategory_id) : "");
      setChildCategoryId(
        d?.child_category_id ? String(d.child_category_id) : "",
      );

      // Sirf applications[0] use ho raha hai
      const app = d?.applications?.[0];

      setApplicationId(app?.id ?? null);
      const loadedTargetType =
        app?.applicable_type ?? d?.target_type ?? "category";

      // Products must always use percentage
      setTargetType(loadedTargetType);
      setType(
        loadedTargetType === "product"
          ? "percentage"
          : (d?.type ?? "percentage"),
      );
      if (loadedTargetType === "category") {
        setCategoryId(app?.applicable_id ? String(app.applicable_id) : "");
      }
    } catch (error) {
      console.error("Load discount error:", error.response?.data || error);
      toast.error("Failed to load discount.");
    } finally {
      setLoadingDiscount(false);
    }
  };

  useEffect(() => {
    fetchMenu();
    if (isEdit) fetchDiscount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // ---------- Derived dropdown data ----------
  const selectedCategory = menuCategories.find(
    (item) => String(getId(item)) === String(categoryId),
  );
  const subcategories =
    selectedCategory?.subcategories ??
    selectedCategory?.sub_categories ??
    selectedCategory?.subCategories ??
    [];

  const selectedSubcategory = subcategories.find(
    (item) => String(getId(item)) === String(subcategoryId),
  );
  const childCategories =
    selectedSubcategory?.children ??
    selectedSubcategory?.child_categories ??
    selectedSubcategory?.childCategories ??
    [];

  const handleDiscountTypeChange = (e) => {
    const value = e.target.value;

    if (targetType === "product") {
      setType("percentage");
      return;
    }

    setType(value);
  };

  const handleTargetTypeChange = (e) => {
    const value = e.target.value;

    setTargetType(value);
    setCategoryId("");
    setSubcategoryId("");
    setChildCategoryId("");

    if (value === "product") {
      setType("percentage");
    }
  };

  const handleCategoryChange = (e) => {
    setCategoryId(e.target.value);
    setSubcategoryId("");
    setChildCategoryId("");
  };

  const handleSubcategoryChange = (e) => {
    setSubcategoryId(e.target.value);
    setChildCategoryId("");
  };

  // ---------- Submit ----------
  const handleSubmit = async (e) => {
    e.preventDefault();

    const trimmedName = name.trim();
    const numAmount = Number(amount);
    const finalType = targetType === "product" ? "percentage" : type;

    if (!trimmedName) return toast.error("Please enter discount name.");
    if (!Number.isFinite(numAmount) || numAmount <= 0)
      return toast.error("Please enter a valid discount amount.");
    if (finalType === "percentage" && numAmount > 100) {
      return toast.error("Percentage discount cannot exceed 100%.");
    }
    if (!startDate || !endDate) return toast.error("Please select both dates.");
    if (dayjs(endDate).isBefore(dayjs(startDate), "day"))
      return toast.error("End date must be on or after the start date.");

    if (targetType === "category" && !categoryId)
      return toast.error("Please select a category.");
    if (targetType === "subcategory" && !subcategoryId)
      return toast.error("Please select a subcategory.");
    if (targetType === "child_category" && !childCategoryId)
      return toast.error("Please select a child category.");

    const payload = {
      name: trimmedName,
      type: finalType,
      amount: numAmount,
      start_date: dayjs(startDate).format("YYYY-MM-DD"),
      end_date: dayjs(endDate).format("YYYY-MM-DD"),
      active: active ? 1 : 0,
      target_type: targetType,
      category_id: ["category", "subcategory", "child_category"].includes(
        targetType,
      )
        ? Number(categoryId)
        : null,
      subcategory_id: ["subcategory", "child_category"].includes(targetType)
        ? Number(subcategoryId)
        : null,
      child_category_id:
        targetType === "child_category" ? Number(childCategoryId) : null,
      product_id: null,
    };

    const applicableId =
      targetType === "category"
        ? categoryId
        : targetType === "subcategory"
          ? subcategoryId
          : targetType === "child_category"
            ? childCategoryId
            : "";

    try {
      setLoading(true);

      Swal.fire({
        title: isEdit ? "Updating Discount..." : "Saving Discount...",
        text: "Please wait.",
        allowOutsideClick: false,
        allowEscapeKey: false,
        background: "#ffffff",
        color: "#dc2626",
        didOpen: () => Swal.showLoading(),
      });

      const headers = { "Content-Type": "application/json" };
      let discountId = id;

      // STEP 1: create or update the discount
      if (isEdit) {
        await axios.put(`${API_BASE}/discounts/${id}`, payload, { headers });
      } else {
        const res = await axios.post(`${API_BASE}/discounts`, payload, {
          headers,
        });
        discountId =
          res.data?.id ??
          res.data?.discount?.id ??
          res.data?.data?.id ??
          res.data?.data?.discount?.id;

        if (!discountId) {
          throw new Error(
            "Discount was created, but its ID was not returned. Please check the API response.",
          );
        }
      }

      // STEP 2: create or update the discount application
      const applicationPayload = {
        discount_id: Number(discountId),
        applicable_type: targetType,
        applicable_id: targetType === "product" ? "" : Number(applicableId),
      };

      if (isEdit && applicationId) {
        await axios.put(
          `${API_BASE}/discount-applications/${applicationId}`,
          applicationPayload,
          { headers },
        );
      } else {
        await axios.post(
          `${API_BASE}/discount-applications`,
          applicationPayload,
          { headers },
        );
      }

      Swal.close();
      await Swal.fire({
        icon: "success",
        title: isEdit ? "Discount Updated" : "Discount Added",
        text: isEdit
          ? "Discount has been updated successfully."
          : "Discount and its application have been saved successfully.",
        confirmButtonColor: "#dc2626",
      });

      navigate("/discounts");
    } catch (error) {
      Swal.close();
      console.error("Discount API error:", error.response?.data || error);
      toast.error(
        error.response?.data?.message ||
          error.message ||
          "Failed to save discount. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  if (loadingDiscount) {
    return (
      <div className="max-w-3xl mx-auto p-8 dark:text-white">
        Loading discount...
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto p-8 rounded-lg shadow-md dark:bg-gray-800 dark:text-white">
      <h1 className="text-3xl font-semibold mb-6 border-b pb-2 text-red-600 dark:border-gray-700">
        {isEdit ? "Edit Discount" : "Add New Discount"}
      </h1>

      <p className="mb-8 text-gray-600 dark:text-gray-300">
        {isEdit
          ? "Update this discount and where it applies."
          : "Create a flat-rate or percentage discount for a product, category, subcategory, or child category."}
      </p>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <div>
            <label className="block mb-2 font-medium">Discount Name</label>
            <Input
              name="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter discount name"
              className="w-full dark:bg-gray-700"
            />
          </div>
          <div>
            <label className="block mb-2 font-medium">Discount Type</label>

            <select
              name="type"
              value={targetType === "product" ? "percentage" : type}
              onChange={handleDiscountTypeChange}
              className="w-full border rounded-md p-2 dark:bg-gray-700"
            >
              <option value="percentage">Percentage (%)</option>

              {targetType !== "product" && (
                <option value="fixed">Fixed Amount (₹)</option>
              )}
            </select>
          </div>

          <div>
            <label className="block mb-2 font-medium">Amount</label>
            <Input
              name="amount"
              type="number"
              min="0.01"
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="Enter amount"
            />
          </div>

          <div>
            <label className="block mb-2 font-medium">Apply Discount To</label>
            <select
              value={targetType}
              onChange={handleTargetTypeChange}
              className="w-full border rounded-md p-2 dark:bg-gray-700"
            >
              <option value="product">Products</option>
              <option value="category">Categories</option>
              <option value="subcategory">Subcategories</option>
              <option value="child_category">Child Categories</option>
            </select>
          </div>
        </div>

        {targetType === "product" && (
          <div>
            <label className="block mb-2 font-medium">
              Selected Discount Target
            </label>
            <div className="w-full border rounded-md p-3 bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-white">
              Products
            </div>
          </div>
        )}

        {targetType !== "product" && (
          <>
            <div>
              <label className="block mb-2 font-medium">Select Category</label>
              <select
                value={categoryId}
                onChange={handleCategoryChange}
                disabled={loadingOptions}
                className="w-full border rounded-md p-2 dark:bg-gray-700"
              >
                <option value="">
                  {loadingOptions ? "Loading categories..." : "Select category"}
                </option>
                {menuCategories.map((item) => (
                  <option key={getId(item)} value={getId(item)}>
                    {getName(item)}
                  </option>
                ))}
              </select>
            </div>

            {["subcategory", "child_category"].includes(targetType) && (
              <div>
                <label className="block mb-2 font-medium">
                  Select Subcategory
                </label>
                <select
                  value={subcategoryId}
                  onChange={handleSubcategoryChange}
                  disabled={!categoryId}
                  className="w-full border rounded-md p-2 dark:bg-gray-700"
                >
                  <option value="">Select subcategory</option>
                  {subcategories.map((item) => (
                    <option key={getId(item)} value={getId(item)}>
                      {getName(item)}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {targetType === "child_category" && (
              <div>
                <label className="block mb-2 font-medium">
                  Select Child Category
                </label>
                <select
                  value={childCategoryId}
                  onChange={(e) => setChildCategoryId(e.target.value)}
                  disabled={!subcategoryId}
                  className="w-full border rounded-md p-2 dark:bg-gray-700"
                >
                  <option value="">Select child category</option>
                  {childCategories.map((item) => (
                    <option key={getId(item)} value={getId(item)}>
                      {getName(item)}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Start Date</label>
            <CustomDatePicker
              name="startDate"
              label="Start Date"
              value={startDate}
              onChange={(value) => setStartDate(value)}
              minDate={isEdit ? undefined : today}
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">End Date</label>
            <CustomDatePicker
              name="endDate"
              label="End Date"
              value={endDate}
              onChange={(value) => setEndDate(value)}
              minDate={isEdit ? undefined : today}
            />
          </div>
        </div>

        <div className="flex items-center gap-3">
          <input
            type="checkbox"
            id="active"
            name="active"
            checked={active}
            onChange={(e) => setActive(e.target.checked)}
            className="h-5 w-5 accent-red-600"
          />
          <label htmlFor="active" className="font-medium">
            Active
          </label>
        </div>

        <div className="flex justify-end gap-4 border-t pt-4 dark:border-gray-700">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate("/discounts")}
          >
            Cancel
          </Button>

          <Button type="submit" disabled={loading}>
            {loading
              ? "Saving..."
              : isEdit
                ? "Update Discount"
                : "Add Discount"}
          </Button>
        </div>
      </form>

      <ToastContainer position="top-right" />
    </div>
  );
};

export default DiscountFormPage;
